// src/utils/generateReportPDF.js
// Генерация PDF-отчёта по складу.
// jsPDF не поддерживает кириллицу из коробки — встроенные шрифты только латиница.
// Решение: используем встроенный шрифт "Helvetica", но текст пишем через
// Unicode-совместимый рендеринг. На самом деле проще всего — подключить
// готовый Roboto.ttf через base64. Чтобы не раздувать бандл, грузим шрифт
// динамически с CDN при первой генерации, кэшируем в памяти.

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

let cachedFont = null;

async function loadCyrillicFont() {
  if (cachedFont) return cachedFont;

  // Roboto-Regular.ttf с jsdelivr (CDN)
  // 168 КБ один раз, потом кэшируется браузером
  const url = 'https://cdn.jsdelivr.net/npm/@fontsource/roboto@4.5.8/files/roboto-cyrillic-400-normal.woff';

  try {
    // Альтернативный путь — TTF напрямую
    const ttfUrl = 'https://cdn.jsdelivr.net/npm/roboto-font@0.1.0/fonts/Roboto/roboto-regular-webfont.ttf';
    const res = await fetch(ttfUrl);
    if (!res.ok) throw new Error('Font fetch failed');
    const buf = await res.arrayBuffer();

    // Конвертируем в base64
    let binary = '';
    const bytes = new Uint8Array(buf);
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    cachedFont = btoa(binary);
    return cachedFont;
  } catch (e) {
    console.warn('Не удалось загрузить шрифт с кириллицей:', e);
    return null;
  }
}

function fmtDate(d) {
  const date = new Date(d);
  return date.toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDateTime(d) {
  const date = new Date(d);
  return date.toLocaleString('ru', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtMoney(n) {
  return Math.round(n).toLocaleString('ru');
}

function fmtQty(n, unit) {
  // округляем до 3 знаков для веса/литров, до целых для штук
  const isInt = unit === 'шт' || Number.isInteger(n);
  const val = isInt ? n : (Math.round(n * 1000) / 1000);
  return `${val} ${unit}`;
}

export async function generateReportPDF(report) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 12;

  // Загружаем кириллический шрифт
  const fontBase64 = await loadCyrillicFont();
  if (fontBase64) {
    doc.addFileToVFS('Roboto-Regular.ttf', fontBase64);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.setFont('Roboto');
  }

  let y = margin;

  // ─── ШАПКА ───
  doc.setFontSize(18);
  doc.text(report.shop.name, margin, y);
  y += 7;
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(`Отчёт по складу за период ${fmtDate(report.period.from)} — ${fmtDate(report.period.to)}`, margin, y);
  y += 5;
  doc.text(`Сформирован: ${fmtDateTime(new Date())}`, margin, y);
  doc.setTextColor(0);
  y += 8;

  // ─── СВОДКА ───
  const s = report.summary;
  const summaryRows = [
    ['Выручка наличными',          `${fmtMoney(s.revenueCash)} сом`],
    ['Выручка баллами',            `${fmtMoney(s.revenuePoints)} сом`],
    ['  — из них доплата налом',   `${fmtMoney(s.cashTopUp)} сом`],
    ['  — баллов списано',         `${s.pointsRedeemed.toLocaleString('ru')}`],
    ['Прибыль (выручка − себест.)',`${fmtMoney(s.profit)} сом`],
    ['Количество продаж',          `${s.salesCount}`],
    ['Закупок на сумму',           `${fmtMoney(s.purchaseTotal)} сом`],
    ['Возвратов на сумму',         `${fmtMoney(s.returnTotal)} сом`],
    ['Списано на сумму',           `${fmtMoney(s.writeoffTotal)} сом`],
  ];

  autoTable(doc, {
    startY: y,
    head: [['Сводка', '']],
    body: summaryRows,
    theme: 'grid',
    headStyles: { fillColor: [99, 102, 241], font: fontBase64 ? 'Roboto' : 'helvetica', fontStyle: 'normal', fontSize: 11 },
    bodyStyles: { font: fontBase64 ? 'Roboto' : 'helvetica', fontStyle: 'normal', fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 90 },
      1: { halign: 'right', cellWidth: 50 },
    },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 8;

  // ─── ТАБЛИЦА ТОВАРОВ ───
  doc.setFontSize(12);
  doc.text('Движение товаров', margin, y);
  y += 4;

  const productRows = report.products.map(p => {
    const u = p.unit;
    return [
      p.name,
      p.purchase.qty > 0 ? fmtQty(p.purchase.qty, u) : '—',
      p.soldCash.qty > 0 ? fmtQty(p.soldCash.qty, u) : '—',
      p.soldCash.amount > 0 ? fmtMoney(p.soldCash.amount) : '—',
      p.soldPoints.qty > 0 ? fmtQty(p.soldPoints.qty, u) : '—',
      p.soldPoints.amount > 0 ? fmtMoney(p.soldPoints.amount) : '—',
      p.writeoff.qty > 0 ? fmtQty(p.writeoff.qty, u) : '—',
      p.return.qty > 0 ? fmtQty(p.return.qty, u) : '—',
      fmtQty(p.currentStock, u),
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [[
      'Товар',
      'Закуплено',
      'Прод.\nзе деньги',
      'Сум.\nза деньги',
      'Прод.\nза баллы',
      'Сум.\nза баллы',
      'Списано',
      'Возврат',
      'Остаток',
    ]],
    body: productRows,
    theme: 'striped',
    headStyles: { fillColor: [99, 102, 241], font: fontBase64 ? 'Roboto' : 'helvetica', fontStyle: 'normal', fontSize: 8, halign: 'center' },
    bodyStyles: { font: fontBase64 ? 'Roboto' : 'helvetica', fontStyle: 'normal', fontSize: 7.5 },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
      7: { halign: 'right' },
      8: { halign: 'right' },
    },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 10;

  // ─── ЗАКУПКИ ───
  if (report.purchases.length > 0) {
    if (y > 240) { doc.addPage(); y = margin; }
    doc.setFontSize(12);
    doc.text(`Закупки (${report.purchases.length})`, margin, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['Дата', 'Товар', 'Кол-во', 'Сумма', 'Комментарий']],
      body: report.purchases.map(op => [
        fmtDateTime(op.createdAt),
        op.productName,
        fmtQty(op.qty, op.unit),
        `${fmtMoney(op.money)} сом`,
        op.reason || '—',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [6, 182, 212], font: fontBase64 ? 'Roboto' : 'helvetica', fontStyle: 'normal', fontSize: 9 },
      bodyStyles: { font: fontBase64 ? 'Roboto' : 'helvetica', fontStyle: 'normal', fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 50 },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // ─── СПИСАНИЯ ───
  if (report.writeoffs.length > 0) {
    if (y > 240) { doc.addPage(); y = margin; }
    doc.setFontSize(12);
    doc.text(`Списания (${report.writeoffs.length})`, margin, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['Дата', 'Товар', 'Кол-во', 'Потеря', 'Причина']],
      body: report.writeoffs.map(op => [
        fmtDateTime(op.createdAt),
        op.productName,
        fmtQty(op.qty, op.unit),
        `${fmtMoney(op.money)} сом`,
        op.reason || '—',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [239, 68, 68], font: fontBase64 ? 'Roboto' : 'helvetica', fontStyle: 'normal', fontSize: 9 },
      bodyStyles: { font: fontBase64 ? 'Roboto' : 'helvetica', fontStyle: 'normal', fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 50 },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // ─── ВОЗВРАТЫ ───
  if (report.returns.length > 0) {
    if (y > 240) { doc.addPage(); y = margin; }
    doc.setFontSize(12);
    doc.text(`Возвраты от клиентов (${report.returns.length})`, margin, y);
    y += 4;

    autoTable(doc, {
      startY: y,
      head: [['Дата', 'Товар', 'Кол-во', 'Сумма возврата', 'Комментарий']],
      body: report.returns.map(op => [
        fmtDateTime(op.createdAt),
        op.productName,
        fmtQty(op.qty, op.unit),
        `${fmtMoney(op.money)} сом`,
        op.reason || '—',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [245, 158, 11], font: fontBase64 ? 'Roboto' : 'helvetica', fontStyle: 'normal', fontSize: 9 },
      bodyStyles: { font: fontBase64 ? 'Roboto' : 'helvetica', fontStyle: 'normal', fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 50 },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
      margin: { left: margin, right: margin },
    });
  }

  // ─── ПОДВАЛ ───
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Alsatuu · стр. ${i} из ${pageCount}`,
      pageW - margin,
      doc.internal.pageSize.getHeight() - 6,
      { align: 'right' }
    );
  }

  // Возвращаем blob
  return doc.output('blob');
}
